import {
    ruCountryBy,
    ruCountryFor,
    ruCountryIn,
    ruCountryOf,
    ruCountryTo,
    ruCountryCase,
} from '@/i18n/countryCases';

const currentYear = () => new Date().getFullYear();

export const country = {
    en: {
        fallbackTitle: 'Country',
        defaultServices: {
            esim: { title: 'eSIM for Travel', description: 'Mobile internet without roaming – connect instantly upon arrival' },
            vpn: { title: 'VPN for Security', description: 'Protect your data and access your favorite services' },
            tours: { title: 'Tours & Excursions', description: 'Best tours from verified operators – book online' },
            transport: { title: 'Transport & Transfers', description: 'Car rental, taxis and public transport – everything for getting around' },
            insurance: { title: 'Travel Insurance', description: 'Travel insurance – protection for your health and luggage during your trip' },
            stay: { title: 'Accommodation', description: 'Hotels, apartments and villas – find the perfect place to stay' },
        },
        anchorLabels: {
            overview: 'Practical info',
            map: 'Interactive map',
            nearby: 'Popular cities',
            services: 'Services',
            airports: 'Transport & airports',
            articles: 'Articles & guides',
            faq: 'FAQ',
        },
        mapLabel: 'Explore',
        mapTitle: (name) => `Interactive Map of ${name}`,
        siteDescription: 'Your ultimate travel companion for exploring the world',
        touristTypes: ['Adventure travelers', 'Cultural tourists', 'Family travelers', 'Business travelers'],
        webPageName: (name) => `${name} Travel Guide – Cities, Tours, eSIM & More`,
        webPageDesc: (name) => `Complete travel guide to ${name}. Discover top destinations, book tours, get eSIM, find transport and accommodation.`,
        destinationDesc: (name) => `Discover ${name}: explore top cities, attractions, and travel services.`,
        citiesListName: (name) => `Popular cities in ${name}`,
        travelActionName: (name) => `Plan your trip to ${name}`,
        travelActionDesc: (name) => `Book tours, transport, accommodation and more for your ${name} trip`,
        airportsListName: (name) => `Airports in ${name}`,
        metaTitleFallback: (name) => `${name} Travel Guide ${currentYear()}: Cities, Tours, eSIM & Tips`,
        metaTitleGeneric: 'Country Travel Guide',
        metaDescFallback: (name) => `Plan your ${name} trip: discover top cities, book tours & excursions, get eSIM for travel, find hotels & transport. Complete ${name} travel guide with practical tips.`,
        metaDescGeneric: 'Explore countries: top cities, airports and travel tips.',
        ogImageAlt: (name) => `${name} - Travel Guide`,
        keywords: (name) => [
            `${name} travel guide`, `${name} tourism`, `visit ${name}`,
            `${name} cities to visit`, `${name} tours`, `eSIM ${name}`,
            `${name} travel tips`, `${name} hotels`, `${name} transport`,
            `things to do in ${name}`, `${name} attractions`, `${name} vacation`,
            `travel to ${name} ${currentYear()}`,
        ],
    },
    ru: {
        fallbackTitle: 'Страна',
        defaultServices: {
            esim: { title: 'eSIM для путешествий', description: 'Мобильный интернет без роуминга – подключайтесь мгновенно по прилёту' },
            vpn: { title: 'VPN для безопасности', description: 'Защитите свои данные и получите доступ к любимым сервисам' },
            tours: { title: 'Экскурсии и туры', description: 'Лучшие туры от проверенных операторов – бронируйте онлайн' },
            transport: { title: 'Транспорт и трансферы', description: 'Аренда авто, такси и общественный транспорт – всё для передвижения' },
            insurance: { title: 'Страховка', description: 'Туристическая страховка – защита здоровья и багажа в путешествии' },
            stay: { title: 'Проживание', description: 'Отели, апартаменты и виллы – найдите идеальное место для проживания' },
        },
        anchorLabels: {
            overview: 'Практическая информация',
            map: 'Интерактивная карта',
            nearby: 'Популярные города',
            services: 'Сервисы',
            airports: 'Транспорт и аэропорты',
            articles: 'Статьи и гайды',
            faq: 'FAQ',
        },
        mapLabel: 'Исследуйте',
        mapTitle: (name) => `Интерактивная карта ${ruCountryOf(name)}`,
        siteDescription: 'Ваш помощник в путешествиях по всему миру',
        touristTypes: ['Любители приключений', 'Культурный туризм', 'Семейный отдых', 'Деловые поездки'],
        webPageName: (name) => `${name} – путеводитель, города, туры и советы`,
        webPageDesc: (name) => `Полный путеводитель ${ruCountryBy(name)}. Откройте лучшие города, бронируйте туры, подключите eSIM, найдите транспорт и жильё.`,
        destinationDesc: (name) => `Откройте ${ruCountryCase(name, 'accusative')}: лучшие города, достопримечательности и сервисы для путешествий.`,
        citiesListName: (name) => `Популярные города ${ruCountryOf(name)}`,
        travelActionName: (name) => `Спланируйте поездку ${ruCountryTo(name)}`,
        travelActionDesc: (name) => `Бронируйте туры, транспорт, жильё и многое другое для путешествия ${ruCountryTo(name)}`,
        airportsListName: (name) => `Аэропорты ${ruCountryOf(name)}`,
        metaTitleFallback: (name) => `${name} ${currentYear()}: путеводитель, города, туры, eSIM и советы`,
        metaTitleGeneric: 'Путеводитель по стране',
        metaDescFallback: (name) => `Планируйте поездку ${ruCountryTo(name)}: откройте лучшие города, бронируйте туры и экскурсии, подключите eSIM, найдите отели и транспорт. Полный путеводитель с практическими советами.`,
        metaDescGeneric: 'Откройте страны: топ города, аэропорты и советы по поездке.',
        ogImageAlt: (name) => `${name} – путеводитель`,
        keywords: (name) => [
            `${name} путеводитель`, `туризм ${ruCountryOf(name)}`, `поездка ${ruCountryTo(name)}`,
            `города ${ruCountryOf(name)}`, `туры ${ruCountryIn(name)}`, `eSIM ${ruCountryFor(name)}`,
            `советы путешественникам ${ruCountryBy(name)}`, `отели ${ruCountryIn(name)}`, `транспорт ${ruCountryIn(name)}`,
            `что посмотреть ${ruCountryIn(name)}`, `достопримечательности ${ruCountryOf(name)}`, `отдых ${ruCountryIn(name)}`,
            `путешествие ${ruCountryTo(name)} ${currentYear()}`,
        ],
    },
};

export const cities = {
    en: {
        defaultMetaTitle: (name) =>
            name ? `Cities of ${name} – resorts and guides` : 'Cities catalog – travel guides',
        defaultMetaDescription: (name) =>
            name
                ? `${name} cities catalog: regions, short guides and highlights.`
                : 'Cities catalog: regions, short guides and highlights.',
        defaultH1: (name) => (name ? `Cities of ${name}` : 'Cities'),
        allLabel: 'All',
        searchPlaceholder: 'Search city\u2026',
        catalogLabel: 'Catalog',
        allCitiesOf: (name) => `All cities of ${name}`,
        breadcrumbCities: 'Cities',
        featuredTitle: 'Featured Cities',
        featuredSubtitle: 'Discover the most popular destinations',
    },
    ru: {
        defaultMetaTitle: (name) =>
            name ? `Города ${name} – курорты и путеводители` : 'Каталог городов – путеводители',
        defaultMetaDescription: (name) =>
            name
                ? `Каталог городов ${name}: регионы, краткие описания и путеводители.`
                : 'Каталог городов: регионы, краткие описания и путеводители.',
        defaultH1: (name) => (name ? `Города ${name}` : 'Города'),
        allLabel: 'Все',
        searchPlaceholder: 'Поиск города\u2026',
        catalogLabel: 'Каталог',
        allCitiesOf: (name) => `Все города ${name}`,
        breadcrumbCities: 'Города',
        featuredTitle: 'Популярные города',
        featuredSubtitle: 'Откройте самые популярные направления',
    },
};

export const cityPage = {
    en: {
        notFoundTitle: 'City not found - TripLinkHub',
        notFoundDescription: 'City page not found',
        defaultCurrency: 'EUR',
        descriptionFallback: (title, countryName) =>
            countryName
                ? `Discover ${title} - one of the most beautiful cities in ${countryName}.`
                : `Discover ${title} - travel guide and tips.`,
        keywords: (title) => [
            title,
            `${title} travel guide`,
            `${title} tourism`,
            `things to do in ${title}`,
            `${title} attractions`,
        ],
    },
    ru: {
        notFoundTitle: 'Город не найден - TripLinkHub',
        notFoundDescription: 'Страница города не найдена',
        defaultCurrency: 'RUB',
        descriptionFallback: (title, countryName) =>
            countryName
                ? `Откройте ${title} - один из самых красивых городов ${ruCountryOf(countryName)}.`
                : `Откройте ${title} - путеводитель и советы для путешествия.`,
        keywords: (title) => [
            title,
            `${title} путеводитель`,
            `${title} туризм`,
            `что посмотреть в ${title}`,
            `достопримечательности ${title}`,
        ],
    },
};

export const poiPage = {
    en: {
        notFoundTitle: 'Place not found - TripLinkHub',
        notFoundDescription: 'Page not found',
        forbiddenTitle: 'Page not found',
        defaultCurrency: 'EUR',
    },
    ru: {
        notFoundTitle: 'Место не найдено - TripLinkHub',
        notFoundDescription: 'Страница не найдена',
        forbiddenTitle: 'Страница не найдена',
        defaultCurrency: 'RUB',
    },
};

export const airportPage = {
    en: {
        notFoundTitle: 'Airport not found - TripLinkHub',
        notFoundDescription: 'Airport page not found',
        forbiddenTitle: 'Page not found',
        defaultDescription: 'Airport information and transport options.',
        keywords: (name, iata, country) => [
            `${name || iata} airport`,
            `${country} airport`,
            `airport ${iata}`,
            `transport from ${name || iata}`,
        ],
    },
    ru: {
        notFoundTitle: 'Аэропорт не найден - TripLinkHub',
        notFoundDescription: 'Страница аэропорта не найдена',
        forbiddenTitle: 'Страница не найдена',
        defaultDescription: 'Информация об аэропорте и вариантах трансфера.',
        keywords: (name, iata, country) => [
            `${name || iata} аэропорт`,
            `${country} аэропорт`,
            `аэропорт ${iata}`,
            `трансфер из ${name || iata}`,
        ],
    },
};
